import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Copy, Check, Loader2, Clock } from "lucide-react";

function useCountdown(expiresAt) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!expiresAt) return;
    const end = new Date(expiresAt).getTime();
    const tick = () => setLeft(Math.max(0, Math.floor((end - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);
  return left;
}

export default function UpiPayDialog({ order, onClose }) {
  const [utr, setUtr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);
  const left = useCountdown(order.expires_at);
  const expired = order.expires_at && left <= 0;
  const mm = String(Math.floor(left / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");

  async function submit() {
    if (!/^[0-9]{12}$/.test(utr.trim())) {
      toast.error("Enter the 12-digit UPI transaction ID (UTR)");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/billing/orders/${order.id}/upi/submit`, { utr: utr.trim() });
      setDone(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  function copyVpa() {
    navigator.clipboard?.writeText(order.vpa);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(15,37,87,.55)" }}>
      <div data-testid="upi-dialog" className="w-full max-w-md rounded-3xl bg-card border border-border shadow-2xl overflow-hidden">
        <div className="px-5 py-4 flex items-center justify-between" style={{ background: "linear-gradient(135deg, hsl(var(--navy)), hsl(var(--cyan)))" }}>
          <div className="text-white">
            <div className="font-semibold text-sm">Pay via UPI · {order.amount_display}</div>
            <div className="text-[10px] opacity-80">Order {order.order_ref}</div>
          </div>
          <button data-testid="upi-close" onClick={onClose} className="text-white/90 p-1 rounded-full hover:bg-white/15"><X className="w-4 h-4" /></button>
        </div>

        {done ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 rounded-full grid place-items-center mx-auto mb-4" style={{ background: "hsl(var(--cyan))" }}><Check className="w-7 h-7 text-white" /></div>
            <div className="font-semibold text-lg" style={{ color: "hsl(var(--navy))" }}>Reference submitted</div>
            <p className="text-sm text-muted-foreground mt-2">You have <b>provisional access for 48 hours</b>. We&apos;ll verify the payment and email your GST tax invoice once confirmed.</p>
            <button data-testid="upi-done-close" onClick={onClose} className="mt-6 inline-flex rounded-full px-6 py-2.5 text-sm font-semibold text-white" style={{ background: "hsl(var(--navy))" }}>Done</button>
          </div>
        ) : (
          <div className="p-5">
            {order.expires_at && (
              <div className={`flex items-center justify-center gap-1.5 text-xs font-semibold mb-3 ${expired ? "text-red-600" : "text-muted-foreground"}`}>
                <Clock className="w-3.5 h-3.5" /> {expired ? "QR expired — you can still pay to the UPI ID below" : `QR valid for ${mm}:${ss}`}
              </div>
            )}
            <p className="text-xs text-muted-foreground text-center mb-3">Scan with any UPI app (GPay, PhonePe, Paytm). Add order ref <b>{order.order_ref}</b> in the note.</p>
            <div className="flex justify-center">
              <img src={order.qr} alt="UPI QR" className="w-48 h-48 rounded-xl border border-border bg-white p-2" data-testid="upi-qr" />
            </div>
            <div className="mt-4 flex items-center justify-between rounded-xl bg-secondary/50 px-4 py-2.5">
              <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Pay to</div><div className="text-sm font-semibold" data-testid="upi-vpa">{order.vpa}</div></div>
              <button onClick={copyVpa} className="p-2 rounded-full hover:bg-background" data-testid="upi-copy-vpa">{copied ? <Check className="w-4 h-4" style={{ color: "hsl(var(--cyan))" }} /> : <Copy className="w-4 h-4" />}</button>
            </div>
            <div className="mt-5">
              <label className="text-xs font-semibold text-foreground/80">After paying, enter your 12-digit UPI transaction ID (UTR)</label>
              <input data-testid="upi-utr-input" value={utr} onChange={(e) => setUtr(e.target.value.replace(/[^0-9]/g, "").slice(0, 12))} inputMode="numeric"
                placeholder="e.g. 429112345678"
                className="mt-1.5 w-full rounded-full border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
              <button data-testid="upi-submit" onClick={submit} disabled={busy}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold text-white disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, hsl(var(--navy)), hsl(var(--cyan)))" }}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} I&apos;ve paid — submit reference
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
